import { lazy, StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import AboutPage from './AboutPage'
import WeeklyMenuActionPage from './WeeklyMenuActionPage'
import AdminAddOnsPage from './admin/AdminAddOnsPage'
import AdminContentMediaPage from './admin/AdminContentMediaPage'
import AdminContentMediaPreviewPage from './admin/AdminContentMediaPreviewPage'
import AdminCustomersPage from './admin/AdminCustomersPage'
import AdminDashboardPage from './admin/AdminDashboardPage'
import AdminDiagnosticsPage from './admin/AdminDiagnosticsPage'
import AdminDiscountCodesPage from './admin/AdminDiscountCodesPage'
import AdminEmailNotificationsPage from './admin/AdminEmailNotificationsPage'
import AdminLoginPage from './admin/AdminLoginPage'
import AdminOrdersPage from './admin/AdminOrdersPage'
import AdminNotFoundPage from './admin/AdminNotFoundPage'
import AdminPricingDeliveryPage from './admin/AdminPricingDeliveryPage'
// Keep reports CSS eager at its original cascade position while deferring the page JavaScript.
import './styles/admin-reports.css'
import AdminReviewsPage from './admin/AdminReviewsPage'
import AdminSecurityPage from './admin/AdminSecurityPage'
import AdminStoreStatusPage from './admin/AdminStoreStatusPage'
import AdminSupportTicketsPage from './admin/AdminSupportTicketsPage'
import AdminSystemSettingsPage from './admin/AdminSystemSettingsPage'
import AdminWeeklyMenuPage from './admin/AdminWeeklyMenuPage'
import { AdminButton, AdminCard, AdminSkeleton, AdminStatePanel } from './admin/AdminUi'
import CheckoutConfirmationPage from './checkout/CheckoutConfirmationPage'
import CookieCarePage from './CookieCarePage'
import FaqPage from './FaqPage'
import HomepageRuntimePage from './HomepageRuntimePage'
import {
  AllergensPage,
  DeliveryTermsPage,
  LegalHubPage,
  PaymentTermsPage,
  PrivacyPolicyPage,
  RefundsPage,
  TermsPage,
} from './LegalPages'
import MenuDependentRuntimePage from './MenuDependentRuntimePage'
import OrderRuntimePage from './OrderRuntimePage'
import PublicNotFoundPage from './PublicNotFoundPage'
import ReviewsPage from './ReviewsPage'
import SupportPage from './SupportPage'
import { applyRouteMetadata } from './routeMetadata'
import './styles/tokens.css'
import './styles/global.css'
import './styles/marketing-email-retirement.css'
import './styles/visible-bounds-asset.css'
import './styles/homepage.css'
import './styles/homepage-desktop-corrections.css'
import './styles/homepage-tablet.css'
import './styles/homepage-mobile.css'
import './styles/homepage-mobile-corrections.css'
import './styles/homepage-product-presentation.css'
import './styles/homepage-interactions.css'
import './styles/homepage-accessibility.css'
import './styles/homepage-owner-corrections.css'
import './styles/homepage-v2.css'
import './styles/homepage-v2-master-corrections.css'
import './styles/homepage-v2-responsive-corrections.css'
import './styles/homepage-v2-hero-artwork-fix.css'
import './styles/homepage-v2-visual-qa-pass.css'
import './styles/homepage-2026-09-10.css'
import './styles/homepage-2026-09-10-desktop-parity.css'
import './styles/homepage-2026-09-10-visible-bounds.css'
import './styles/homepage-v3.css'
import './styles/homepage-v3-micro-corrections.css'
import './styles/homepage-v3-responsive-header.css'
import './styles/homepage-v3-mobile-responsive-corrections.css'
import './styles/public-error-404.css'
import './styles/faq.css'
import './styles/reviews.css'
import './styles/about.css'
import './styles/legal.css'
import './styles/content-media-product-presentation.css'
import './styles/analytics-consent.css'
import './styles/site-announcement.css'
import './styles/flavour-quick-view.css'
import './styles/order.css'
import './styles/order-support-stack.css'
import './styles/order-visual-parity.css'
import './styles/order-reference-corrections.css'
import './styles/order-approved-assets-render.css'
import './styles/checkout-confirmation-mobile-fix.css'
import './styles/admin-login.css'
import './styles/admin-login-qr.css'
import './styles/admin-shell.css'
import './styles/admin-shell-devil-hold-corrections.css'
import './styles/admin-dashboard.css'
import './styles/admin-store-status.css'
import './styles/admin-content-media-enhancements.css'
import './styles/admin-reviews.css'

const LazyAdminReportsPage = lazy(() => import('./admin/AdminReportsPage').catch(() => ({
  default: () => (
    <main className="admin-shell-gate-error">
      <AdminStatePanel
        state="error"
        title="Reports unavailable"
        description="Reports could not be loaded. Please try again."
        action={<AdminButton type="button" onClick={() => window.location.reload()}>Retry</AdminButton>}
      />
    </main>
  ),
})))

function AdminReportsPage() {
  return (
    <Suspense fallback={
      <main className="admin-shell-gate-loading" aria-label="Loading Boss Zone Reports" role="status">
        <AdminCard>
          <AdminSkeleton lines={8} />
        </AdminCard>
      </main>
    }>
      <LazyAdminReportsPage />
    </Suspense>
  )
}

applyRouteMetadata()

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Application root element was not found.')

const path = window.location.pathname.replace(/\/+$/, '') || '/'
const RootApp = path === '/'
  ? HomepageRuntimePage
  : path === '/order'
    ? OrderRuntimePage
    : path === '/cart'
      ? () => <MenuDependentRuntimePage target="cart" />
      : path === '/checkout'
        ? () => <MenuDependentRuntimePage target="checkout" />
        : path === '/checkout/confirmation'
          ? CheckoutConfirmationPage
          : path === '/cookie-care'
            ? CookieCarePage
            : path === '/about'
            ? AboutPage
            : path === '/reviews'
              ? ReviewsPage
              : path === '/faq'
                ? FaqPage
              : path === '/legal'
                ? LegalHubPage
                : path === '/terms'
                  ? TermsPage
                  : path === '/refunds'
                    ? RefundsPage
                    : path === '/delivery-terms'
                      ? DeliveryTermsPage
                      : path === '/payment-terms'
                        ? PaymentTermsPage
                        : path === '/privacy'
                          ? PrivacyPolicyPage
                          : path === '/weekly-menu/confirm'
                            ? () => <WeeklyMenuActionPage />
                            : path === '/weekly-menu/unsubscribe'
                              ? () => <WeeklyMenuActionPage />
                            : path === '/allergens'
                            ? AllergensPage
                            : path === '/contact'
                              ? SupportPage
                              : path === '/admin/login'
                                ? AdminLoginPage
                                : path === '/admin'
                                  ? AdminDashboardPage
                                  : path === '/admin/store-status'
                                    ? AdminStoreStatusPage
                                    : path === '/admin/orders'
                                      ? AdminOrdersPage
                                      : path === '/admin/customers'
                                        ? AdminCustomersPage
                                        : path === '/admin/weekly-menu'
                                          ? AdminWeeklyMenuPage
                                          : path === '/admin/add-ons'
                                            ? AdminAddOnsPage
                                          : path === '/admin/discount-codes'
                                            ? AdminDiscountCodesPage
                                            : path === '/admin/pricing-delivery'
                                              ? AdminPricingDeliveryPage
                                              : path === '/admin/support'
                                                ? AdminSupportTicketsPage
                                                : path === '/admin/email-notifications'
                                                  ? AdminEmailNotificationsPage
                                                  : path === '/admin/reports'
                                                    ? AdminReportsPage
                                                    : path === '/admin/reviews'
                                                      ? AdminReviewsPage
                                                      : path === '/admin/diagnostics'
                                                      ? AdminDiagnosticsPage
                                                      : path === '/admin/settings'
                                                        ? AdminSystemSettingsPage
                                                        : path === '/admin/security'
                                                          ? AdminSecurityPage
                                                          : path === '/admin/content-media/preview'
                                                            ? AdminContentMediaPreviewPage
                                                            : path === '/admin/content-media'
                                                              ? AdminContentMediaPage
                                                              : path.startsWith('/admin/')
                                                                ? AdminNotFoundPage
                                                                : PublicNotFoundPage

createRoot(rootElement).render(
  <StrictMode>
    <RootApp />
  </StrictMode>,
)
